<template>
    <GuidedSetupChrome ref="chrome" label="Coaching setup" :show-overview="setup.activeStep.value === null" @escape="setup.closeStep">
        <!-- Keep the live region mounted outside the chrome's conditionals so screen transitions update its
             text without recreating it.
             -->
        <template #status>
            <output class="sr-only" aria-label="Setup status">{{ screenStatus }}</output>
        </template>

        <template #overview>
            <ContextSummary
                ref="overview"
                :steps="setup.steps.value"
                :context="setup.context.value"
                :completed-count="setup.completedCount.value"
                :readings="setup.readings.value"
                :readings-loading="setup.readingsLoading.value"
                :chat-input="chatInput"
                @open="setup.openStep" />
        </template>

        <template #sections>
            <BaseSectionNav label="Coaching setup sections" :items="navItems" :model-value="setup.activeStep.value ?? undefined" @update:model-value="openStep" />
        </template>

        <template #step>
            <GuidedSetupStepRunner v-if="setup.activeStep.value" :key="setup.activeStep.value" :setup="setup" :step-id="setup.activeStep.value" @scroll-reset="resetScroll" />
        </template>
    </GuidedSetupChrome>
</template>

<script setup lang="ts">
import BaseSectionNav from "~vue/components/BaseSectionNav.vue";
import ContextSummary from "~vue/ProfileSetup/ContextSummary.vue";
import GuidedSetupChrome from "~vue/ProfileSetup/GuidedSetupChrome.vue";
import GuidedSetupStepRunner from "~vue/ProfileSetup/GuidedSetupStepRunner.vue";
import { SETUP_SECTION_ICONS } from "~vue/ProfileSetup/setupSections";
import type { ChatInputProps, SetupStep, SetupStepId } from "~vue/ProfileSetup/types";
import type { ProfileSetup } from "~vue/ProfileSetup/useProfileSetup";
import { computed, nextTick, onMounted, useTemplateRef, watch } from "vue";

const { setup, chatInput = null } = defineProps<{
    setup: ProfileSetup;
    /** Null for hosts without chat configuration, including the dev gallery. */
    chatInput?: ChatInputProps | null;
}>();
const chrome = useTemplateRef<{ focusPage: () => void; resetScroll: () => Promise<void> }>("chrome");
const overview = useTemplateRef<{ focus: () => void }>("overview");
onMounted(() => chrome.value?.focusPage());

/*
 * Closing an ask removes the focused "All asks" button. Focus the overview to preserve the keyboard user's
 * position.
 */
watch(
    () => setup.activeStep.value,
    async (step, previous) => {
        if (step !== null || previous === null) {
            return;
        }
        await nextTick();
        overview.value?.focus();
    },
);

/* Steps can be opened in any order, so announce their names rather than ordinal positions. */
const screenStatus = computed(() => {
    const step = setup.activeStep.value;
    return step === null ? "Coaching setup overview" : `${stepLabel(step)} — coaching setup`;
});

/*
 * Readings are absent from page props. Fetch on arrival and when setup becomes complete so newly finished
 * users see them without reloading.
 */
watch(
    () => setup.steps.value.every((step) => step.done),
    (complete) => {
        if (complete) {
            setup.loadReadings();
        }
    },
    { immediate: true },
);

const navItems = computed(() =>
    setup.steps.value.map((step) => {
        const blocker = setup.blockedBy(step.id);
        return {
            id: step.id,
            label: step.label,
            icon: SETUP_SECTION_ICONS[step.id],
            completed: step.done,
            disabled: blocker !== null,
            note: blocker === null ? undefined : `Complete ${stepLabel(blocker)} first`,
            accessibleLabel: `${step.label}: ${statusLabel(step)}`,
        };
    }),
);

function stepLabel(id: SetupStepId): string {
    return setup.steps.value.find((step) => step.id === id)?.label ?? "";
}

function openStep(id: string | undefined): void {
    const step = setup.steps.value.find((step) => step.id === id);
    if (step) {
        setup.openStep(step.id);
    }
}

function statusLabel(step: SetupStep): string {
    if (step.done) {
        return "Done";
    }
    const blocker = setup.blockedBy(step.id);
    if (blocker !== null) {
        return `Locked until ${stepLabel(blocker)} is done`;
    }
    return step.id === setup.activeStep.value ? "In progress" : "Not started";
}

async function resetScroll(): Promise<void> {
    await chrome.value?.resetScroll();
}
</script>
